// === resolvePath ===
// Input: groupSlug (string), slugPath (string), groups.json, pages.json
// Output: page object or null
export function resolvePath(groupSlug, slugPath, groups, pages) {
  console.log('[resolvePath] Called with:', { groupSlug, slugPath, groupsCount: groups.length, pagesCount: pages.length });

  const group = groups.find(g => g.slug === groupSlug);
  if (!group) {
    console.warn("[resolvePath] Group not found:", groupSlug);
    return null;
  }

  console.log('[resolvePath] Found group:', { id: group.id, slug: group.slug, title: group.title });

  const slugs = slugPath?.split('/').filter(Boolean) ?? [];
  const candidates = pages.filter(p => p.group_id === group.id);

  console.log('[resolvePath] Candidates:', { slugs, candidatesCount: candidates.length, candidateSlugs: candidates.map(c => c.slug) });

  if (slugs.length === 0) {
    const result = candidates.find(p => p.slug === "home" && p.parent_id == null) || null;
    console.log('[resolvePath] No slugs, returning home page:', result);
    return result;
  }

  // Try root match first
  let current = candidates.find(p => p.slug === slugs[0] && p.parent_id == null);
  console.log('[resolvePath] Root match for', slugs[0], ':', current);

  // If not found, try as child of 'home'
  if (!current) {
    const home = candidates.find(p => p.slug === "home" && p.parent_id == null);
    console.log('[resolvePath] Home page:', home);
    current = candidates.find(p => p.slug === slugs[0] && p.parent_id === home?.id);
    console.log('[resolvePath] As child of home:', current);
    if (!current) {
      console.warn("[resolvePath] Could not resolve as child of home:", slugs[0]);
      return null;
    }
  }

  if (!current) {
    console.warn("[resolvePath] Root page not found:", slugs[0]);
    return null;
  }

  // Walk tree if more than one slug
  for (let i = 1; i < slugs.length; i++) {
    console.log('[resolvePath] Looking for child:', slugs[i], 'under parent:', current.id, current.slug);
    const next = candidates.find(p => p.slug === slugs[i] && p.parent_id === current.id);
    console.log('[resolvePath] Found child:', next);
    if (!next) {
      console.warn("[resolvePath] Child page not found at slug:", slugs[i], "under parent:", current);
      return null;
    }
    current = next;
  }

  console.log('[resolvePath] Final result:', current);
  return current;
}

// === buildPath ===
// Input: page object, groups.json, pages.json
// Output: "/groupSlug/parentSlug/childSlug/..."
export function buildPath(page, groups, pages) {
  if (!page) return null;

  const group = groups.find(g => g.id === page.group_id);
  if (!group) return null;

  const byId = new Map(pages.map(p => [p.id, p]));

  const chain = [];
  let current = page;
  while (current) {
    chain.push(current.slug);
    current = current.parent_id != null ? byId.get(current.parent_id) : null;
  }
  chain.reverse();

  // TODO: Make sure your page hierarchy in the database matches the path you want to use.
  // If you want /home/budgets/budgets-5-lane to work, "budgets" must be a child of "home",
  // and "budgets-5-lane" must be a child of "budgets".
  // UNTIL THEN, since the backend has fallback logic of, if it can't find the full path,
  // it tries to drop the first segment after "home" and look for the rest as a direct child of "home".
  // So, "home/budgets-5-lane" works if "budgets-5-lane" is a direct child of "${group.slug}/home".

  // use the first and last entries of chain with a '/' between them. when the DB hierarchy matches the desired URL structure, we can remove this special case handling.
  if (chain.length > 1) {
    return `/${group.slug}/${chain[0]}/${chain[chain.length - 1]}`;
  }

  return `/${group.slug}/${chain.join('/')}`;
}

// === buildBreadcrumb ===
// Input: page object, groups.json, pages.json
// Output: [{ title, path }]
export function buildBreadcrumb(page, groups, pages) {
  if (!page) return [];

  const breadcrumb = [];

  let current = page;
  while (current) {
    breadcrumb.unshift({
      title: current.title,
      path: `/${groups.find(g => g.id === current.group_id)?.slug || ""}/${current.slug}`,
      children: (current.children || []).map((childId) => {
        const childPage = pages.find((p) => p.id === childId);
        return childPage
          ? {
              title: childPage.title,
              path: `/${groups.find(g => g.id === childPage.group_id)?.slug || ""}/${childPage.slug}`
            }
          : null;
      }).filter(Boolean),
    });
    current = pages.find((p) => p.id === current.parent_id);
  }

  return breadcrumb;
}




